import { fetchJSON as fetchAPI } from './client';

export type HealthScoreComponent = {
  key: string; name: string; current_value?: string; target?: string; gap?: string;
  priority?: string; business_impact?: string; evidence?: string[];
};

export type InventoryHealthScore = {
  asin: string; product_name?: string; overall_score: number;
  components: HealthScoreComponent[]; dna_origin: boolean; generated_at: string;
};

export type DemandForecast = {
  asin: string; expected_units: number; expected_revenue: string; expected_profit: string;
  confidence: number; forecast_drivers?: string[]; alternative_low?: number;
  alternative_high?: number; prediction_range?: string; generated_at: string;
};

export type VelocityInsight = { period: string; units: number; trend?: string; stability?: string };

export type ReplenishmentPlan = {
  asin: string; should_replenish: boolean; recommended_qty: number; recommended_date?: string;
  reason?: string; expected_revenue?: string; expected_profit?: string;
  cash_requirement?: string; working_capital_impact?: string; confidence: number;
};

export type ShipmentPlan = {
  id: string; asin: string; quantity: number; destination_fc: string;
  expected_arrival?: string; estimated_cost?: string; revenue_impact?: string; status: string;
};

export type ScenarioPlan = {
  id: string; name: string; type: string; units: number; expected_revenue?: string;
  expected_profit?: string; cash_requirement?: string; stockout_probability?: string;
  overstock_probability?: string; confidence: number; key_assumptions?: string[];
};

export type InventoryRecommendation = {
  id: string; title: string; category: string; business_objective: string;
  evidence?: string[]; root_cause?: string; expected_revenue?: string; expected_profit?: string;
  expected_inventory_improvement?: string; expected_cash_flow_impact?: string;
  confidence: number; priority: string; effort: string; risk: string;
  status: string; action_label: string; dna_evidence: boolean;
};

export type ShipmentSimulationResult = {
  asin: string; plan_id: string; arrival_date?: string; stockout_probability?: string;
  storage_fees?: string; inventory_days: number; expected_sales?: string;
  expected_profit?: string; cash_requirement?: string; confidence: number; key_assumptions?: string[];
};

export type OpportunityInsight = {
  id: string; title: string; type: string; expected_profit?: string; priority: string; confidence: number;
};

export type PlanVersion = {
  id: string; asin: string; version: number; type: string; status: string;
  author?: string; approved: boolean; created_at: string; summary?: string;
};

export type ExperimentLink = {
  id: string; asin: string; name: string; status: string;
  baseline_kpi?: string; observation_window?: string; created_at: string;
};

export type InventoryTimelineEvent = {
  id: string; asin: string; type: string; title: string; actor?: string; timestamp: string;
};

const COMMAND_BASE = import.meta.env.VITE_COMMAND_API ?? '/api/v1/command';
const fetchCmd = <T,>(path: string, init?: RequestInit) => fetchAPI<T>(`${COMMAND_BASE}${path}`, init);

export const fbaGrowthIntelligenceApi = {
  health: (asin: string) => fetchCmd<InventoryHealthScore>(`/fba-growth/${encodeURIComponent(asin)}/health`),
  forecast: (asin: string) => fetchCmd<DemandForecast>(`/fba-growth/${encodeURIComponent(asin)}/forecast`),
  velocity: (asin: string) =>
    fetchCmd<{ velocity: VelocityInsight[] }>(`/fba-growth/${encodeURIComponent(asin)}/velocity`).then((r) => r.velocity),
  replenishment: (asin: string) => fetchCmd<ReplenishmentPlan>(`/fba-growth/${encodeURIComponent(asin)}/replenishment`),
  shipments: (asin: string) =>
    fetchCmd<{ shipments: ShipmentPlan[] }>(`/fba-growth/${encodeURIComponent(asin)}/shipments`).then((r) => r.shipments),
  scenarios: (asin: string) =>
    fetchCmd<{ scenarios: ScenarioPlan[] }>(`/fba-growth/${encodeURIComponent(asin)}/scenarios`).then((r) => r.scenarios),
  simulate: (asin: string, planId: string) =>
    fetchCmd<ShipmentSimulationResult>(`/fba-growth/${encodeURIComponent(asin)}/simulate`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ plan_id: planId }),
    }),
  recommendations: (asin: string) =>
    fetchCmd<{ recommendations: InventoryRecommendation[] }>(`/fba-growth/${encodeURIComponent(asin)}/recommendations`).then((r) => r.recommendations),
  opportunities: (asin: string) =>
    fetchCmd<{ opportunities: OpportunityInsight[] }>(`/fba-growth/${encodeURIComponent(asin)}/opportunities`).then((r) => r.opportunities),
  versions: (asin: string) =>
    fetchCmd<{ versions: PlanVersion[] }>(`/fba-growth/${encodeURIComponent(asin)}/versions`).then((r) => r.versions),
  experiments: (asin: string) =>
    fetchCmd<{ experiments: ExperimentLink[] }>(`/fba-growth/${encodeURIComponent(asin)}/experiments`).then((r) => r.experiments),
  history: (asin: string) =>
    fetchCmd<{ history: InventoryTimelineEvent[] }>(`/fba-growth/${encodeURIComponent(asin)}/history`).then((r) => r.history),
};
